import React, { useState, useCallback } from 'react';
import { Upload, FileText, CheckCircle, AlertCircle } from 'lucide-react';
import toolkitService from '../../services/ToolkitService';
import Spinner from '../shared/Spinner';

// Toolkit step: upload an Opera v5 report and parse it on the server. Shared by every Toolkit screen that starts
// from a file — the desktop app's "Select file" button.
//
// The file goes straight to S3 (presigned PUT — reports outgrow API Gateway's body limit); `parse(key)` then turns
// the S3 key into rows and resolves { data, message }. Pass `kind` instead to use the generic /toolkit/files/parse.
//
// `parse` may simply return the key ({ data: key }) for a report the server reads later (comparisons).
// beforeUpload(): optional async gate (e.g. AR Accounts validates the property's settings first); return false to stop.

export const STEP = { IDLE: 'idle', BUSY: 'busy', DONE: 'done', ERROR: 'error' };

export const stepIcon = (state) =>
    state === STEP.BUSY ? <Spinner />
        : state === STEP.DONE ? <CheckCircle size={16} className="tkb-ok" />
            : state === STEP.ERROR ? <AlertCircle size={16} className="tkb-fail" /> : null;

const errorOf = (err, fallback) => err?.response?.data?.message || err?.response?.data?.error?.message || err?.message || fallback;

function ToolkitFileStep({ number = 2, title, label, accept = '.xml,.rep,.txt', kind, parseOptions, parse, onParsed, beforeUpload, disabled, children }) {
    const [state, setState] = useState({ state: STEP.IDLE, message: '' });

    const onFile = useCallback(async (e) => {
        const file = e.target.files && e.target.files[0];
        e.target.value = '';                 // the same file can be chosen again after a fix
        if (!file) return;
        if (beforeUpload && !(await beforeUpload())) return;
        setState({ state: STEP.BUSY, message: `Uploading ${file.name}…` });
        try {
            const key = await toolkitService.upload(file, (pct) =>
                setState({ state: STEP.BUSY, message: `Uploading ${file.name}… ${pct}%` }));
            setState({ state: STEP.BUSY, message: 'Parsing…' });
            const parsed = parse
                ? await parse(key)
                : await toolkitService.parseFile(key, kind, parseOptions).then(({ data }) => ({
                    data: data.data, message: `${data.count} row(s) from ${file.name}`,
                }));
            setState({ state: STEP.DONE, message: parsed.message });
            onParsed(parsed.data, { file, key });
        } catch (err) {
            setState({ state: STEP.ERROR, message: errorOf(err, 'Upload failed') });
        }
    }, [beforeUpload, kind, parse, onParsed, parseOptions]);

    return (
        <div className="tkb-step">
            <div className="tkb-step-head"><span className="tkb-num">{number}</span> {title} {stepIcon(state.state)}</div>
            <label className={`tkb-file${disabled ? ' tkb-file-disabled' : ''}`}>
                <Upload size={15} /> {label}
                <input type="file" accept={accept} onChange={onFile} hidden disabled={disabled} />
            </label>
            {state.message && (
                <p className={`tkb-msg${state.state === STEP.ERROR ? ' tkb-fail' : ''}`}>
                    <FileText size={13} /> {state.message}
                </p>
            )}
            {children}
        </div>
    );
}

export default ToolkitFileStep;
